import { BrandLogo } from "@/components/ui/brand-logo";
import { cn } from "@/lib/utils";
import { LoadingSpinner } from "./loading-spinner";

interface LoadingPageProps {
  /** Mensagem opcional por baixo (ex.: "A autenticar…"). */
  message?: string;
  /** Ecrã inteiro (arranque da app) ou bloco embutido numa página. */
  fullScreen?: boolean;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
}

/**
 * O ecrã de espera.
 *
 * Em ecrã inteiro — que é o arranque da aplicação — mostra a marca com uma
 * barra de progresso indeterminada por baixo. Um spinner sozinho no meio do
 * vazio lê-se como "isto encravou"; a marca e uma barra a andar leem-se como
 * "a aplicação está a abrir". É a mesma cena do ecrã de entrada, e a passagem
 * de um para o outro deixa de ter um salto.
 *
 * Embutido numa página, onde já há contexto à volta, basta o arco a rodar.
 */
export function LoadingPage({
  message,
  fullScreen = true,
  className,
  size = "md",
}: LoadingPageProps) {
  if (!fullScreen) {
    return (
      <div className={cn("flex flex-col items-center justify-center gap-3 py-16", className)}>
        <LoadingSpinner size={size} />
        {message && <p className="text-[13px] text-muted-foreground">{message}</p>}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-dvh flex-col items-center justify-center gap-8 bg-background",
        className
      )}
      role="status"
      aria-live="polite"
    >
      <BrandLogo className="h-12 w-auto max-w-[260px] object-contain" fallbackClassName="text-2xl" />

      <div className="flex flex-col items-center gap-4">
        {/* A calha e o traço que a atravessa. `overflow-hidden` é o que recorta
            o traço nas pontas — sem isso ele passeia-se pelo ecrã fora. */}
        <div className="h-px w-40 overflow-hidden rounded-full bg-foreground/8">
          <div className="h-full w-1/4 animate-indeterminate rounded-full bg-primary" />
        </div>

        {message && (
          <p className="text-[13px] tracking-tight text-muted-foreground">{message}</p>
        )}
      </div>
    </div>
  );
}
